import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BellIcon,
  UserCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../Authentication/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import { FiTag, FiUsers, FiGift } from "react-icons/fi";
import { useGetVendorKYCStatusQuery } from "../../redux/service/vendorService";
import LogoutConfirmationModal from "../Authentication/LogoutConfirmationModal";
import { useDispatch, useSelector } from "react-redux";
import { setVendorIsApproved } from "../../redux/slice/upSwapSlice";
import { FaStoreAlt } from "react-icons/fa";
import { BookOpenIcon } from "lucide-react";

import MobileUserHeader from "./MobileUserHeader"; // Import the new component

// A simple hook to check screen width
export const useIsMobile = (breakpoint = 1024) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};
const Maps_API_KEY = "AIzaSyCRQXge0eI79YVPiihiUrqOKv4NapMnA9I";

const UserHeader = () => {
  const isMobile = useIsMobile();
  const { logout, userRole, setUserRole, userData: loginUserData } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false);

  const [currentLocation, setCurrentLocation] = useState(() => {
    const storedLocation = localStorage.getItem("userLocation");
    return storedLocation
      ? JSON.parse(storedLocation)
      : {
          city: "",
          state: "",
          latitude: null,
          longitude: null,
          country: "",
          area: "",
          formattedAddress: "",
        };
  });

  const [manualLocationInput, setManualLocationInput] = useState("");
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const locationInputModalRef = useRef(null);
  const autocompleteInstance = useRef(null);
  const vendorId = localStorage.getItem("vendor_id");
  const { vendorIsApproved } = useSelector((state) => state.upSwap);

  const initMap = useCallback(() => {
    setIsGoogleMapsLoaded(true);
  }, []);

  const extractLocationDetails = (addressComponents) => {
    let city = "";
    let state = "";
    let country = "";
    let area = "";
    let postalCode = "";

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) {
        city = component.long_name;
      } else if (types.includes("administrative_area_level_1")) {
        state = component.long_name;
      } else if (types.includes("country")) {
        country = component.long_name;
      } else if (types.includes("postal_code")) {
        postalCode = component.long_name;
      }
      const areaTypes = [
        "neighborhood",
        "sublocality_level_1",
        "sublocality",
        "postal_town",
        "route",
        "political",
      ];

      if (!area) {
        for (const type of areaTypes) {
          if (types.includes(type)) {
            area = component.long_name;
            break;
          }
        }
      }
    });

    return { city, state, country, area, postalCode };
  };

  const getAddressFromCoordinates = useCallback(
    async (latitude, longitude) => {
      if (!isGoogleMapsLoaded || !window.google?.maps?.Geocoder) {
        toast.error("Google Maps API not fully loaded. Please try again.");
        return null;
      }

      const geocoder = new window.google.maps.Geocoder();
      const latlng = { lat: latitude, lng: longitude };

      try {
        const response = await geocoder.geocode({ location: latlng });

        if (!response || !Array.isArray(response.results)) {
          console.error("Reverse geocoding failed. Response:", response);
          toast.error("Failed to fetch address details. Please try again.");
          return null;
        }

        const { results } = response;

        if (results?.[0]) {
          const { address_components, formatted_address } = results[0];
          const { city, state, country, area, postalCode } =
            extractLocationDetails(address_components);

          return {
            city: city || "Unknown City",
            state: state || "Unknown Region",
            latitude,
            longitude,
            country: country || "Unknown Country",
            area: area || postalCode || "",
            formattedAddress: formatted_address || "",
          };
        } else {
          toast.warning(
            "Could not find address details for your current location."
          );
          return {
            city: "Location Found",
            state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
            latitude,
            longitude,
            country: "",
            area: "",
            formattedAddress: "",
          };
        }
      } catch (error) {
        console.error("Error reverse geocoding:", error);
        toast.error("Failed to fetch address details. Please try again.");
        return null;
      }
    },
    [isGoogleMapsLoaded]
  );

  const handleManualLocationSubmit = useCallback(
    async (fromAutocompleteSelection = false) => {
      const input = manualLocationInput.trim();
      if (!input) {
        if (!fromAutocompleteSelection) {
          toast.warning("Please enter a location.");
        }
        return;
      }

      if (isGoogleMapsLoaded && window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        try {
          const { results } = await geocoder.geocode({ address: input });

          if (results?.[0]) {
            const { geometry, address_components, formatted_address } =
              results[0];
            const { city, state, country, area } =
              extractLocationDetails(address_components);
            const { location } = geometry;

            const newLocation = {
              city: city || input.split(",")[0].trim(),
              state,
              latitude: location.lat(),
              longitude: location.lng(),
              country,
              area: area || "",
              formattedAddress: formatted_address || input,
            };

            setCurrentLocation(newLocation);
            localStorage.setItem("userLocation", JSON.stringify(newLocation));
            setShowLocationModal(false);
            toast.success("Location updated successfully!");
            return;
          }
        } catch (error) {
          console.error("Geocoding error:", error);
        }
      }

      const [cityPart, ...rest] = input.split(",").map((p) => p.trim());
      const newLocation = {
        city: cityPart,
        state: rest.join(", "),
        latitude: null,
        longitude: null,
        country: "",
        area: "",
        formattedAddress: input,
      };

      setCurrentLocation(newLocation);
      localStorage.setItem("userLocation", JSON.stringify(newLocation));
      setShowLocationModal(false);
      toast.success("Location set successfully!");
    },
    [manualLocationInput, isGoogleMapsLoaded]
  );

  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    window.initMap = initMap;

    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js"]`
    );

    if (existingScript) {
      existingScript.onload = () => setIsGoogleMapsLoaded(true);
      existingScript.onerror = () =>
        toast.error("Failed to load Google Maps API.");
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${Maps_API_KEY}&libraries=places&callback=initMap`;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-script-header";
    script.onerror = () => toast.error("Failed to load Google Maps API.");
    document.head.appendChild(script);

    return () => {
      if (window.initMap === initMap) delete window.initMap;
    };
  }, [initMap]);

  useEffect(() => {
    if (
      !showLocationModal ||
      !isGoogleMapsLoaded ||
      !locationInputModalRef.current
    ) {
      return;
    }

    if (!autocompleteInstance.current) {
      try {
        autocompleteInstance.current =
          new window.google.maps.places.Autocomplete(
            locationInputModalRef.current,
            { types: ["geocode", "establishment"] }
          );

        autocompleteInstance.current.addListener("place_changed", () => {
          const place = autocompleteInstance.current.getPlace();

          if (place.geometry?.location && place.formatted_address) {
            const { address_components } = place;
            const { city, state, country, area } =
              extractLocationDetails(address_components);

            const newLocation = {
              city: city || place.formatted_address.split(",")[0].trim(),
              state,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              country,
              area: area || "",
              formattedAddress: place.formatted_address,
            };

            setCurrentLocation(newLocation);
            localStorage.setItem("userLocation", JSON.stringify(newLocation));
            setManualLocationInput(place.formatted_address);
            setShowLocationModal(false);
            toast.success("Location updated!");
          } else {
            toast.warning(
              "No detailed information from autocomplete. Using manual entry."
            );
            handleManualLocationSubmit(true);
          }
        });
      } catch (error) {
        console.error("Autocomplete error:", error);
        toast.error(
          "Failed to initialize location suggestions. Please try manual entry."
        );
      }
    }

    return () => {
      if (autocompleteInstance.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteInstance.current
        );
      }
      autocompleteInstance.current = null;
    };
  }, [isGoogleMapsLoaded, showLocationModal, handleManualLocationSubmit]);

  useEffect(() => {
    if (!navigator.geolocation || !isGoogleMapsLoaded) return;

    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const locationDetails = await getAddressFromCoordinates(
          latitude,
          longitude
        );
        if (locationDetails) {
          setCurrentLocation(locationDetails);
          localStorage.setItem("userLocation", JSON.stringify(locationDetails));
        }
      },
      (error) => {
          toast.error("Failed to fetch your location.");
      }
    );
  }, [isGoogleMapsLoaded, getAddressFromCoordinates]);

  useEffect(() => {
    document.body.style.overflow =
      showLocationModal || showLogoutConfirmModal ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showLocationModal, showLogoutConfirmModal]);

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser.");
      return;
    }
    if (!isGoogleMapsLoaded) {
      toast.info("Loading location services...");
      return;
    }

    toast.info("Fetching your current location...");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const locationDetails = await getAddressFromCoordinates(
          latitude,
          longitude
        );

        if (locationDetails) {
          setCurrentLocation(locationDetails);
          localStorage.setItem("userLocation", JSON.stringify(locationDetails));
          toast.success("Location updated successfully!");
        } else {
          const fallbackLocation = {
            city: "Unknown Address",
            state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
            latitude,
            longitude,
            country: "",
            area: "",
            formattedAddress: "",
          };
          setCurrentLocation(fallbackLocation);
          localStorage.setItem(
            "userLocation",
            JSON.stringify(fallbackLocation)
          );
        }
        setShowLocationModal(false);
      },
      (error) => {
        const errorMessages = {
          [error.PERMISSION_DENIED]:
            "Location access denied. Please enable location services.",
          [error.POSITION_UNAVAILABLE]: "Location information unavailable.",
          [error.TIMEOUT]: "Location request timed out.",
        };
        toast.error(
          errorMessages[error.code] || "Unable to retrieve your location."
        );
      }
    );
  };

  const navItems = [
    {
      name: "Activities",
      icon: FiUsers,
      dropdownItems: [
        { name: "Browse Activities", path: "/LocalActivities" },
        { name: "Create Activity", path: "/PostActivities" },
        { name: "My Created Activities", path: "/MyActivities" },
      ],
    },
    {
      name: "Services",
      icon: FiGift,
      dropdownItems: [
        { name: "Browse Services", path: "/services/browse" },
        { name: "Favorite Services", path: "/services/favorite" },
      ],
    },
    {
      name: "Deals",
      icon: FiTag,
      dropdownItems: [
        { name: "Browse Deals", path: "/PopularDeals" },
        { name: "My Orders", path: "/mypurchasedeal" },
      ],
    },
    {
      name: "Vendors",
      icon: FaStoreAlt,
      dropdownItems: [
        { name: "Browse Vendors", path: "/VendorList" },
        { name: "Favorite Vendors", path: "/Favorite" },
      ],
    },
    {
      name: "Bookings",
      icon: BookOpenIcon,
      dropdownItems: [{ name: "My Bookings", path: "/customer/bookings" }],
    },
  ];

  const confirmLogout = () => {
    logout();
    localStorage.clear();
    setUserRole("public");
    navigate("/");
    setShowLogoutConfirmModal(false);
    toast.success("Logged out successfully!");
  };

  const cancelLogout = () => {
    setShowLogoutConfirmModal(false);
    toast.info("Logout cancelled.");
  };

  const handleLogoutClick = (e) => {
    e.stopPropagation();
    setIsUserDropdownOpen(false);
    setShowLogoutConfirmModal(true);
  };

  const handleUserDropdownToggle = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setOpenNavDropdown(null);
  };

  const handleNavDropdownToggle = (index) => {
    setOpenNavDropdown(openNavDropdown === index ? null : index);
    setIsUserDropdownOpen(false);
  };

  const handleDropdownItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setOpenNavDropdown(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isUserDropdownOpen &&
        !event.target.closest(".user-dropdown-container")
      ) {
        setIsUserDropdownOpen(false);
      }
      if (
        openNavDropdown !== null &&
        !event.target.closest(".nav-dropdown-container")
      ) {
        setOpenNavDropdown(null);
      }
      if (
        showLocationModal &&
        !event.target.closest(".location-modal-container")
      ) {
        setShowLocationModal(false);
      }
      if (
        showLogoutConfirmModal &&
        !event.target.closest(".logout-modal-container")
      ) {
        setShowLogoutConfirmModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isUserDropdownOpen,
    openNavDropdown,
    showLocationModal,
    showLogoutConfirmModal,
  ]);

  const handleBecomeAVendorClick = (e) => {
    e.stopPropagation();
    const phoneNumber = localStorage.getItem("phone_number");
    const email = localStorage.getItem("email");

    if (!phoneNumber || !email) {
      toast.info(
        "Please update your user profile first, and then complete KYC."
      );
      setIsUserDropdownOpen(false);
      return;
    }

    navigate("/BasicInfo");
    setIsUserDropdownOpen(false);
  };

  const {
    data: kycData,
    isLoading: kycDataIsLoading,
    isFetching: kycDataIsFetching,
  } = useGetVendorKYCStatusQuery(vendorId, { skip: !vendorId });

  useEffect(() => {
    if (!kycDataIsFetching && !kycDataIsLoading) {
      dispatch(setVendorIsApproved(kycData?.is_approved));
    }
  }, [kycDataIsLoading, kycDataIsFetching, kycData, dispatch]);

  // Conditional Rendering Logic
  if (isMobile) {
    return (
      <>
        <MobileUserHeader
          navItems={navItems}
          loginUserData={loginUserData}
          vendorIsApproved={vendorIsApproved}
          userRole={userRole}
          setUserRole={setUserRole}
          handleLogoutClick={handleLogoutClick}
          handleBecomeAVendorClick={handleBecomeAVendorClick}
          currentLocation={currentLocation} // <-- Pass prop
          setShowLocationModal={setShowLocationModal} // <-- Pass prop
        />

        {/* The Location Modal logic remains here to be shared */}
        {showLocationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 location-modal-container">
            <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-sm mx-4 md:max-w-md md:p-8">
              <button
                onClick={() => setShowLocationModal(false)}
                className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100"
              >
                <FaTimes size={20} />
              </button>

              <h2 className="text-xl font-bold mb-4">Select Your Location</h2>

              <button
                onClick={handleUseCurrentLocation}
                disabled={!isGoogleMapsLoaded}
                className={`w-full py-2 px-4 rounded-md mb-4 ${
                  !isGoogleMapsLoaded
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                <FaMapMarkerAlt className="inline-block mr-2" />
                {isGoogleMapsLoaded ? "Use Current Location" : "Loading..."}
              </button>

              <div className="text-center text-gray-500 mb-4 font-semibold">
                - OR -
              </div>

              <input
                type="text"
                ref={locationInputModalRef}
                placeholder="Enter location (e.g., New York, USA)"
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 mb-4"
                value={manualLocationInput}
                onChange={(e) => setManualLocationInput(e.target.value)}
              />

              <button
                onClick={() => handleManualLocationSubmit()}
                className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600"
              >
                Set Location
              </button>
            </div>
          </div>
        )}

        {showLogoutConfirmModal && (
          <LogoutConfirmationModal
            onConfirm={confirmLogout}
            onCancel={cancelLogout}
            onClose={cancelLogout}
          />
        )}
      </>
    );
  }

  return (
    <>
      <header className="bg-white shadow-sm py-4 px-6 border-b border-gray-200 hidden lg:block">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex-shrink-0 cursor-pointer">
            <img
              src="/upswap.png"
              alt="UpSway Logo"
              className="h-10"
              onClick={() => navigate("/LocalActivities")}
            />
          </div>

          <div
            className="flex items-center text-gray-700 hover:text-black text-sm font-medium cursor-pointer ml-4"
            onClick={() => setShowLocationModal(true)}
          >
            <FaMapMarkerAlt className="h-5 w-5 text-gray-500 mr-2" />
            <div className="flex flex-col">
              <div className="font-semibold leading-none">
                {currentLocation.city || "Select Location"}
              </div>
              <div className="text-xs text-gray-500 leading-none">
                {[currentLocation.area, currentLocation.state]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            </div>
          </div>

          {showLocationModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 location-modal-container">
              <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-sm mx-4 md:max-w-md md:p-8">
                <button
                  onClick={() => setShowLocationModal(false)}
                  className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100"
                >
                  <FaTimes size={20} />
                </button>

                <h2 className="text-xl font-bold mb-4">Select Your Location</h2>

                <button
                  onClick={handleUseCurrentLocation}
                  disabled={!isGoogleMapsLoaded}
                  className={`w-full py-2 px-4 rounded-md mb-4 ${
                    !isGoogleMapsLoaded
                      ? "bg-blue-300 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  <FaMapMarkerAlt className="inline-block mr-2" />
                  {isGoogleMapsLoaded ? "Use Current Location" : "Loading..."}
                </button>

                <div className="text-center text-gray-500 mb-4 font-semibold">
                  - OR -
                </div>

                <input
                  type="text"
                  ref={locationInputModalRef}
                  placeholder="Enter location (e.g., New York, USA)"
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 mb-4"
                  value={manualLocationInput}
                  onChange={(e) => setManualLocationInput(e.target.value)}
                />

                <button
                  onClick={() => handleManualLocationSubmit()}
                  className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600"
                >
                  Set Location
                </button>
              </div>
            </div>
          )}

          {showLogoutConfirmModal && (
            <LogoutConfirmationModal
              onConfirm={confirmLogout}
              onCancel={cancelLogout}
              onClose={cancelLogout}
            />
          )}

          <nav className="hidden lg:flex flex-grow justify-center space-x-8">
            {navItems.map((item, index) => (
              <div
                key={index}
                className="relative text-gray-700 hover:text-black font-semibold cursor-pointer flex items-center text-sm nav-dropdown-container"
                onClick={() => handleNavDropdownToggle(index)}
              >
                {item.icon && (
                  <item.icon className="h-4 w-4 mr-1 text-gray-600 group-hover:text-black" />
                )}
                <span>{item.name}</span>
                <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
                    openNavDropdown === index ? "flex flex-col gap-2" : "hidden"
                  }`}
                >
                  {item.dropdownItems.map((dItem) => (
                    <Link
                      key={dItem.name}
                      to={dItem.path || "#"}
                      className={`block px-4 py-2 text-sm relative ${
                        dItem.commingsoon
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (dItem.commingsoon) return;
                        handleDropdownItemClick(dItem);
                      }}
                    >
                      {dItem.commingsoon && (
                        <span className="absolute top-1 left-4 text-xs text-orange-500 font-semibold">
                          Coming Soon
                        </span>
                      )}
                      <span className={dItem.commingsoon ? "pt-2 block" : ""}>
                        {dItem.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {loginUserData?.vendor_id && vendorIsApproved && (
            <button className="relative flex items-center px-1 py-2 rounded-full bg-gray-100/80 hover:bg-gray-100 transition-all duration-300 mr-8">
              <div
                className={`absolute h-7 w-[90px] top-1 transition-all duration-500 ease-in-out ${
                  userRole === "vendor" ? "left-2" : "left-[75px]"
                }`}
              >
                <div className="h-full w-[67px] rounded-full bg-white shadow-[0_2px_10px_-3px_rgba(0,0,0,0.1)] border border-gray-100" />
              </div>
              <div className="relative z-10 flex items-center">
                <span
                  onClick={() => {
                    localStorage.setItem("userRole", "vendor");
                    setUserRole("vendor");
                    navigate("/vendor/dashboard");
                  }}
                  className={`px-4 text-sm font-medium transition-colors duration-300 ${
                    userRole === "vendor"
                      ? "text-orange-600"
                      : "text-gray-400 hover:text-gray-500"
                  }`}
                >
                  Vendor
                </span>
                <span
                  onClick={() => {
                    localStorage.setItem("userRole", "user");
                    setUserRole("user");
                  }}
                  className={`px-4 text-sm font-medium transition-colors duration-300 ${
                    userRole === "user"
                      ? "text-orange-600"
                      : "text-gray-400 hover:text-gray-500"
                  }`}
                >
                  User
                </span>
              </div>
            </button>
          )}

          <div className="flex items-center space-x-4 ml-auto">
            <div
              className="relative cursor-pointer text-gray-600 hover:text-black"
              onClick={() => toast.info("Notifications coming soon!")}
            >
              <BellIcon className="h-6 w-6" />
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 border border-white"></span>
            </div>

            <div
              className="relative cursor-pointer text-gray-600 hover:text-black flex items-center user-dropdown-container"
              onClick={handleUserDropdownToggle}
            >
              <UserCircleIcon className="h-7 w-7" />
              <ChevronDownIcon className="h-4 w-4 ml-1 text-gray-400" />
              <div
                className={`absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 ${
                  isUserDropdownOpen ? "block" : "hidden"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/UserProfile");
                    setIsUserDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  User Profile
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/mypurchasedeal");
                    setIsUserDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  My Orders
                </button>

                {!vendorId && (
                  <button
                    onClick={handleBecomeAVendorClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Become a vendor
                  </button>
                )}

                {vendorId && !vendorIsApproved && (
                  <button
                    onClick={handleBecomeAVendorClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Become a vendor
                    <p className="text-yellow-600 text-xs font-semibold">
                      Pending
                    </p>
                  </button>
                )}

                {vendorId && vendorIsApproved && (
                  <button
                    onClick={handleBecomeAVendorClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Become a vendor
                    <p className="text-green-600 text-xs font-semibold">
                      Approved{" "}
                      <span className="text-orange-500">(ReApply)</span>
                    </p>
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/Settings");
                    setIsUserDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Settings
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/ContactSupport");
                    setIsUserDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Help & Support
                </button>

                <button
                  onClick={handleLogoutClick}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default UserHeader;
